import type { ParticipantDto } from '@lila/shared';
import { useMutation, useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Alert } from '../../../components/ui/Alert';
import { Button } from '../../../components/ui/Button';
import { AudioRoomProvider } from '../../../features/audio/AudioRoomProvider';
import { useBoard } from '../../../features/board/api';
import { Board } from '../../../features/board/Board';
import { ChatStoreProvider } from '../../../features/chat/ChatStoreProvider';
import { CommBlock } from '../../../features/comm/CommBlock';
import { useEndSession } from '../../../features/session/commands';
import { ConnectionBanner } from '../../../features/session/ConnectionBanner';
import { useReducedMotion } from '../../../features/session/motion';
import { ParticipantList } from '../../../features/session/ParticipantList';
import { SessionSocketProvider } from '../../../features/session/SessionSocketProvider';
import {
  SessionStoreProvider,
  useSessionStore,
} from '../../../features/session/SessionStoreProvider';
import { TurnIndicator } from '../../../features/session/TurnIndicator';
import { sessionQueryKey, useSession } from '../../../features/session/useSession';
import { InviteByEmailCard } from '../../../features/studio/InviteByEmailCard';
import { JoinCodeCard } from '../../../features/studio/JoinCodeCard';
import { OverrideDialog, type OverrideKind } from '../../../features/studio/OverrideDialog';
import { SeatOrder } from '../../../features/studio/SeatOrder';
import { errorKeyFor } from '../../../lib/errors';
import { isLocale } from '../../../lib/i18n';
import { trpcClient } from '../../../lib/trpc';

/**
 * Spec 12.2 `/:lang/studio/sessions/:id/manage — live facilitator control`.
 *
 * "WS, same room as players." The facilitator sees every event the players do, which is
 * what makes the roster live, and the board is READ-ONLY for them: spec 1.1 is "1 human
 * host + up to 7 players", so the host facilitates rather than plays.
 */
export const Route = createFileRoute('/$lang/studio/sessions/$id/manage')({ component: Page });

function Page(): React.ReactNode {
  const { lang, id } = Route.useParams();

  /* Effective chat state from the shared `session.get` cache — same key the board uses. */
  const chatEnabled =
    useQuery({
      queryKey: sessionQueryKey(id),
      queryFn: () => trpcClient.session.get.query({ sessionId: id }),
      staleTime: Infinity,
      retry: false,
    }).data?.chatEnabled ?? false;

  /* `studio/route.tsx` already wraps this tree in `StudioLayout`; wrapping again nested
   * one chrome inside another, complete with a second header and a second skip link. */
  return (
    <SessionStoreProvider sessionId={id}>
      <ChatStoreProvider sessionId={id}>
        <SessionSocketProvider sessionId={id} chatEnabled={chatEnabled}>
          <AudioRoomProvider sessionId={id}>
            <ConnectionBanner />
            <Manage sessionId={id} lang={lang} />
          </AudioRoomProvider>
        </SessionSocketProvider>
      </ChatStoreProvider>
    </SessionStoreProvider>
  );
}

function Manage({
  sessionId,
  lang,
}: {
  readonly sessionId: string;
  readonly lang: string;
}): React.ReactNode {
  const { t } = useTranslation(['studio', 'board', 'common']);
  const locale = isLocale(lang) ? lang : 'en';
  const reducedMotion = useReducedMotion();

  const { session, error } = useSession(sessionId);
  const board = useBoard(locale, session?.boardId);

  const status = useSessionStore((state) => state.status);
  const participants = useSessionStore((state) => state.participants);
  const players = useSessionStore((state) => state.players);
  const activeParticipantId = useSessionStore((state) => state.activeParticipantId);
  const lastSeq = useSessionStore((state) => state.lastSeq);

  const [override, setOverride] = useState<OverrideKind | null>(null);
  const [confirmingEnd, setConfirmingEnd] = useState(false);
  const endSession = useEndSession();

  const start = useMutation({
    mutationFn: () =>
      trpcClient.session.start.mutate({
        sessionId,
        commandId: crypto.randomUUID(),
        expectedSeq: lastSeq,
      }),
    retry: false,
  });

  if (error !== null && error !== undefined) {
    return (
      <div className="mx-auto max-w-xl p-6">
        <Alert tone="error">{t(errorKeyFor(error))}</Alert>
      </div>
    );
  }

  if (session === null || board.data === undefined) {
    return (
      <div className="mx-auto max-w-xl p-6">
        <p className="text-sm text-ink-muted">{t('board:session.loading')}</p>
      </div>
    );
  }

  const seated = participants.filter((p) => p.role === 'player' && p.status !== 'removed');
  const boardPlayers = seated
    .map((participant) => {
      const state = players[participant.id];
      return state === undefined
        ? null
        : {
            participantId: participant.id,
            displayName: participant.displayName,
            seatOrder: participant.seatOrder,
            state,
          };
    })
    .filter((player): player is NonNullable<typeof player> => player !== null);

  return (
    <div className="mx-auto flex w-full max-w-6xl flex-col gap-6 p-4 lg:flex-row lg:p-6">
      <div className="min-w-0 flex-1">
        <p className="mb-3 text-xs text-ink-muted">{t('studio:session.readOnly')}</p>
        <Board
          board={board.data}
          players={boardPlayers}
          selectedSquare={null}
          /* Read-only: spec 12.2 gives the studio `Board72` with no controls. */
          onSelectSquare={() => undefined}
          focusSquare={null}
          reducedMotion={reducedMotion}
        />
      </div>

      <aside className="flex w-full flex-col gap-4 lg:max-w-sm">
        <TurnIndicator meParticipantId={null} />

        {session.code !== null && status === 'lobby' && (
          <JoinCodeCard code={session.code} lang={lang} />
        )}

        {/* Spec 8.2 `session.invite`: reach someone not on the call. Host + join-code only;
            the server rejects solo and closed sessions, matching this render gate. */}
        {session.code !== null && (status === 'lobby' || status === 'active') && (
          <InviteByEmailCard sessionId={sessionId} />
        )}

        <ParticipantList meParticipantId={null} activeParticipantId={activeParticipantId} />

        {/* Comm block (audio + chat): the host is in the same room from the manage screen. */}
        {(session.audioEnabled || session.chatEnabled) && (
          <CommBlock
            meParticipantId={null}
            externalCallUrl={session.externalCallUrl}
            audioEnabled={session.audioEnabled}
            chatEnabled={session.chatEnabled}
          />
        )}

        {status === 'lobby' && seated.length === 0 && (
          <p className="text-sm text-ink-muted">{t('studio:session.lobbyEmpty')}</p>
        )}

        {/* ADR-0010 / spec 6.3: a reorder is permitted in the lobby and nowhere else. */}
        {status === 'lobby' && <SeatOrder sessionId={sessionId} participants={seated} />}

        {status === 'lobby' && (
          <>
            <Button
              type="button"
              loading={start.isPending}
              disabled={seated.length === 0}
              onClick={() => {
                start.mutate();
              }}
            >
              {start.isPending ? t('studio:session.starting') : t('studio:session.start')}
            </Button>
            {start.isError && <Alert tone="error">{t(errorKeyFor(start.error))}</Alert>}
          </>
        )}

        {status === 'active' && (
          <section className="flex flex-col gap-2 rounded-lg border border-border p-4">
            <Button
              type="button"
              variant="secondary"
              disabled={activeParticipantId === null}
              onClick={() => {
                setOverride({ kind: 'SKIP_TURN' });
              }}
            >
              {t('studio:override.skipTurn')}
            </Button>

            {seated.map((participant: ParticipantDto) => (
              <div key={participant.id} className="flex gap-2">
                <Button
                  type="button"
                  variant="secondary"
                  className="flex-1"
                  onClick={() => {
                    setOverride({ kind: 'OVERRIDE_POSITION', participant });
                  }}
                >
                  {participant.displayName} · {t('studio:override.position')}
                </Button>
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => {
                    setOverride({ kind: 'REMOVE_PARTICIPANT', participant });
                  }}
                >
                  {t('studio:override.remove')}
                </Button>
              </div>
            ))}
          </section>
        )}

        {status !== 'completed' && status !== 'abandoned' && (
          <section className="flex flex-col gap-2">
            {confirmingEnd ? (
              <>
                <Alert tone="info">{t('studio:override.endBody')}</Alert>
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={() => {
                      setConfirmingEnd(false);
                    }}
                  >
                    {t('studio:override.cancel')}
                  </Button>
                  <Button
                    type="button"
                    loading={endSession.isPending}
                    onClick={() => {
                      endSession.mutate({ sessionId });
                    }}
                  >
                    {t('studio:override.endConfirm')}
                  </Button>
                </div>
              </>
            ) : (
              <Button
                type="button"
                variant="secondary"
                onClick={() => {
                  setConfirmingEnd(true);
                }}
              >
                {t('studio:session.end')}
              </Button>
            )}
            {endSession.isError && <Alert tone="error">{t(errorKeyFor(endSession.error))}</Alert>}
          </section>
        )}

        {(status === 'completed' || status === 'abandoned') && (
          <Alert tone="info">{t('board:session.ended')}</Alert>
        )}
      </aside>

      <OverrideDialog
        sessionId={sessionId}
        request={override}
        lastSquare={board.data.lastSquare}
        onClose={() => {
          setOverride(null);
        }}
      />
    </div>
  );
}
